import React from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
interface AccessDistributionData {
  ide_only: number;
  web_only: number;
  both: number;
}

interface AccessDistributionProps {
  distribution: AccessDistributionData;
}

const AccessDistribution: React.FC<AccessDistributionProps> = ({ distribution }) => {
  const mappedData = [
    { type: 'IDE', count: distribution.ide_only },
    { type: 'Web', count: distribution.web_only },
    { type: 'Both', count: distribution.both },
  ];

  const total = mappedData.reduce((sum, item) => sum + item.count, 0);

  const getPercentage = (count: number) => {
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };

  const getAccessTypeColor = (type: string) => {
    switch (type) {
      case 'IDE':
        return 'bg-primary-400';
      case 'Web':
        return 'bg-primary-500';
      case 'Both':
        return 'bg-primary-600';
      default:
        return 'bg-primary-200';
    }
  };

  return (
    <div className="chart-container">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
        Access Distribution
      </h3>

      <div className="space-y-4">
        {mappedData.map((item, index) => {
          const percentage = getPercentage(item.count);
          return (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${getAccessTypeColor(item.type)}`} />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.type}
                  </span>
                </div>
                <span className="text-sm font-medium text-primary-500">
                  {percentage}%
                </span>
              </div>
              <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`absolute top-0 left-0 h-full ${getAccessTypeColor(item.type)} rounded-full transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {item.count.toLocaleString()} users
              </div>
            </div>
          );
        })}
      </div>

      {/* Distribution Summary */}
      <div className="mt-6 p-4 rounded-xl bg-primary-500/10 border border-primary-500/20">
        <div className="flex items-center gap-2 text-sm">
          <ChartBarIcon className="h-4 w-4 text-primary-500" />
          <span className="font-medium text-primary-500">
            Primary Access:
          </span>
          <span className="text-gray-700 dark:text-gray-300">
            {mappedData.length > 0 
              ? mappedData.reduce((prev, current) => 
                  prev.count > current.count ? prev : current
                ).type
              : 'No data'
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default AccessDistribution;
